// useSheetKit — Google-Sheets-like focus, keyboard nav, copy/paste TSV, autofill, undo/redo
// Shared between ProjectView and ScopeView.
//
// Consumer contract:
//   columnDefs: [
//     {
//       key: "keyword" | "custom.<id>" | ...,
//       label: string,
//       type: "text" | "number" | "date" | "select" | "user" | "bool" | "url",
//       editable: boolean,
//       options?: string[],           // for select
//       userList?: Array<{id,name}>,  // for user
//       getVal: (task) => any,        // raw value
//       setValInto: (v, task) => patchObject,   // build a task patch to apply this value
//       textOf?: (task, ctx?) => string,        // for copy (default: String(getVal))
//       parseText?: (text, ctx?) => any | undefined,  // for paste (undefined = skip)
//     }, ...
//   ]
//   rows: task[]  (filtered, in display order)
//   canEdit: boolean
//   onPatchTask: async (task, patch) => updatedTask
//   onCreateTask?: async (partial) => createdTask   (needed for paste-extends-rows)
//   onDeleteTasks?: async (taskIds, snapshots) => void
//
// Returns: state + handlers, plus keyboard/paste handler binders.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

export default function useSheetKit({
  rows,
  columnDefs,
  canEdit = true,
  onPatchTask,
  onBulkPatch,          // async (updates:[{task_id, patch}]) => updated tasks[]  — batches into one API call
  onCreateTask,
  onCreateManyTasks,    // async (count, position) => tasks[] — batches N empty-row creates into ONE call (avoids ingress 429s)
  onDeleteTasks,
  context = {},
  richLinks = false,      // opt-in (marketing sheet): preserve hyperlinks from pasted HTML as markdown [label](url)
} = {}) {
  const [focus, setFocus] = useState(null);       // {r, c}
  const [rangeEnd, setRangeEnd] = useState(null); // {r, c}
  const [editing, setEditing] = useState(null);   // {r, c} — cell in inline edit mode
  const [autofill, setAutofill] = useState(null); // {fromR, fromC, toR} — active drag preview
  const dragSelRef = useRef(false);
  const undoStack = useRef([]);
  const redoStack = useRef([]);

  // Reset focus/range when rows change to something incompatible
  useEffect(() => {
    setFocus((f) => (f && rows[f.r] ? f : null));
    setRangeEnd((re) => (re && rows[re.r] ? re : null));
    setEditing((e) => (e && rows[e.r] ? e : null));
    setAutofill(null);
  }, [rows.length]);

  const nRows = rows.length;
  const nCols = columnDefs.length;

  const rangeNorm = useMemo(() => {
    if (!focus) return null;
    const end = rangeEnd || focus;
    return {
      r1: Math.min(focus.r, end.r),
      c1: Math.min(focus.c, end.c),
      r2: Math.max(focus.r, end.r),
      c2: Math.max(focus.c, end.c),
    };
  }, [focus, rangeEnd]);

  const isFocused = useCallback((r, c) => focus && focus.r === r && focus.c === c, [focus]);
  const isInRange = useCallback((r, c) => {
    if (!rangeNorm) return false;
    return r >= rangeNorm.r1 && r <= rangeNorm.r2 && c >= rangeNorm.c1 && c <= rangeNorm.c2;
  }, [rangeNorm]);
  const isAutofillPreview = useCallback((r, c) => {
    if (!autofill) return false;
    const { fromR, fromC, toR } = autofill;
    return c === fromC && r > fromR && r <= toR;
  }, [autofill]);

  const rangeSize = () => {
    if (!rangeNorm) return 0;
    return (rangeNorm.r2 - rangeNorm.r1 + 1) * (rangeNorm.c2 - rangeNorm.c1 + 1);
  };
  const isBottomRightOfRange = useCallback((r, c) => {
    if (!rangeNorm) return false;
    return r === rangeNorm.r2 && c === rangeNorm.c2;
  }, [rangeNorm]);

  // ---------- Undo / Redo ------------
  // Contract: pushUndo({ revert, redo, label? }) — revert reverses the op, redo re-applies it.
  const pushUndo = useCallback((op) => {
    undoStack.current.push(op);
    if (undoStack.current.length > 30) undoStack.current.shift();
    redoStack.current = [];
  }, []);
  const undo = useCallback(async () => {
    const op = undoStack.current.pop();
    if (!op) { toast.message("Nothing to undo"); return; }
    try {
      await op.revert();
      redoStack.current.push(op);
      toast.success(op.label ? `Undone: ${op.label}` : "Undone");
    } catch (e) { toast.error("Undo failed"); }
  }, []);
  const redo = useCallback(async () => {
    const op = redoStack.current.pop();
    if (!op) { toast.message("Nothing to redo"); return; }
    try {
      if (op.redo) await op.redo();
      undoStack.current.push(op);
      toast.success(op.label ? `Redone: ${op.label}` : "Redone");
    } catch (e) { toast.error("Redo failed"); }
  }, []);

  // ---------- Movement ------------
  const clampCell = (r, c) => ({ r: Math.max(0, Math.min(nRows - 1, r)), c: Math.max(0, Math.min(nCols - 1, c)) });
  const moveFocus = useCallback((dr, dc, opts = {}) => {
    setFocus((prev) => {
      if (!prev) {
        if (nRows > 0 && nCols > 0) return { r: 0, c: 0 };
        return prev;
      }
      return clampCell(prev.r + dr, prev.c + dc);
    });
    if (!opts.extend) setRangeEnd(null);
    else setRangeEnd((re) => {
      const base = re || focus || { r: 0, c: 0 };
      return clampCell(base.r + dr, base.c + dc);
    });
    setEditing(null);
    setAutofill(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nRows, nCols, focus]);

  // ---------- Cell mouse handlers ------------
  const onCellMouseDown = useCallback((r, c, e) => {
    // Right-click (button===2): preserve range if cell is inside; else focus just this cell
    if (e.button === 2) {
      if (rangeNorm && r >= rangeNorm.r1 && r <= rangeNorm.r2 && c >= rangeNorm.c1 && c <= rangeNorm.c2) {
        return; // keep the existing selection so the context menu can operate on the range
      }
      setFocus({ r, c });
      setRangeEnd(null);
      return;
    }
    const additive = e.shiftKey;
    if (additive && focus) {
      setRangeEnd({ r, c });
    } else {
      setFocus({ r, c });
      setRangeEnd(null);
    }
    setEditing(null);
    setAutofill(null);
    dragSelRef.current = true;
  }, [focus, rangeNorm]);
  const onCellMouseEnter = useCallback((r, c) => {
    if (!dragSelRef.current) return;
    setRangeEnd({ r, c });
  }, []);
  useEffect(() => {
    const onUp = () => { dragSelRef.current = false; };
    window.addEventListener("mouseup", onUp);
    return () => window.removeEventListener("mouseup", onUp);
  }, []);

  // ---------- Patch helpers (with undo) ------------
  const patchOneCell = useCallback(async (r, c, newVal) => {
    const task = rows[r];
    const col = columnDefs[c];
    if (!task || !col || !col.editable) return;
    const oldVal = col.getVal(task);
    if (oldVal === newVal) return;
    const patch = col.setValInto(newVal, task);
    await onPatchTask(task, patch);
    pushUndo({
      label: `edit ${col.label}`,
      revert: async () => { await onPatchTask(task, col.setValInto(oldVal, task)); },
      redo:   async () => { await onPatchTask(task, col.setValInto(newVal, task)); },
    });
  }, [rows, columnDefs, onPatchTask, pushUndo]);

  // Clear all cells in current range (Delete/Backspace on focused cell)
  const clearRange = useCallback(async () => {
    if (!rangeNorm || !canEdit) return;
    const patches = [];
    for (let r = rangeNorm.r1; r <= rangeNorm.r2; r++) {
      for (let c = rangeNorm.c1; c <= rangeNorm.c2; c++) {
        const task = rows[r];
        const col = columnDefs[c];
        if (!task || !col || !col.editable) continue;
        const oldVal = col.getVal(task);
        const emptyVal = emptyValueForCol(col);
        if (oldVal === emptyVal) continue;
        patches.push({ task, col, oldVal });
      }
    }
    if (patches.length === 0) return;
    // Merge patches per task
    const perTask = {};
    for (const p of patches) {
      const cur = perTask[p.task.id] || { task: p.task, patch: {}, inverse: {} };
      const emptyPatch = p.col.setValInto(emptyValueForCol(p.col), cur.task);
      const invPatch = p.col.setValInto(p.oldVal, cur.task);
      cur.patch = mergePatch(cur.patch, emptyPatch);
      cur.inverse = mergePatch(cur.inverse, invPatch);
      perTask[p.task.id] = cur;
    }
    const patchList = Object.values(perTask).map((p) => ({ task_id: p.task.id, patch: p.patch }));
    const invList = Object.values(perTask).map((p) => ({ task_id: p.task.id, patch: p.inverse }));
    if (onBulkPatch && patchList.length > 0) await onBulkPatch(patchList);
    else for (const { task, patch } of Object.values(perTask)) await onPatchTask(task, patch);
    pushUndo({
      label: `clear ${patches.length} cell${patches.length === 1 ? "" : "s"}`,
      revert: async () => {
        if (onBulkPatch && invList.length > 0) await onBulkPatch(invList);
        else for (const { task, inverse } of Object.values(perTask)) await onPatchTask(task, inverse);
      },
      redo: async () => {
        if (onBulkPatch && patchList.length > 0) await onBulkPatch(patchList);
        else for (const { task, patch } of Object.values(perTask)) await onPatchTask(task, patch);
      },
    });
  }, [rangeNorm, rows, columnDefs, canEdit, onPatchTask, onBulkPatch, pushUndo]);

  // ---------- Copy ------------
  const copySelection = useCallback(async () => {
    if (!rangeNorm) return false;
    const lines = [];
    for (let r = rangeNorm.r1; r <= rangeNorm.r2; r++) {
      const cells = [];
      for (let c = rangeNorm.c1; c <= rangeNorm.c2; c++) {
        const task = rows[r];
        const col = columnDefs[c];
        if (!task || !col) { cells.push(""); continue; }
        const text = col.textOf ? col.textOf(task, context) : safeText(col.getVal(task));
        cells.push(text);
      }
      lines.push(cells.join("\t"));
    }
    const tsv = lines.join("\n");
    try {
      // Iter 45.57 — rich copy (marketing sheet): cells store links as
      // markdown `[label](url)`. On copy, emit BOTH a clean plain-text TSV
      // (markdown stripped to just the label) and a text/html variant with
      // real <a> anchors, so pasting into Google Sheets / docs / back into
      // this sheet keeps the hyperlink intact instead of leaking markdown.
      if (richLinks && typeof ClipboardItem !== "undefined" && /\[[^\]]+\]\([^)\s]+\)/.test(tsv)) {
        const plain = lines.map((l) => l.split("\t").map(mdToPlain).join("\t")).join("\n");
        const html = "<table>" + lines.map((l) =>
          "<tr>" + l.split("\t").map((cell) => `<td>${mdToHtml(cell)}</td>`).join("") + "</tr>"
        ).join("") + "</table>";
        await navigator.clipboard.write([new ClipboardItem({
          "text/plain": new Blob([plain], { type: "text/plain" }),
          "text/html": new Blob([html], { type: "text/html" }),
        })]);
      } else {
        await navigator.clipboard.writeText(richLinks ? lines.map((l) => l.split("\t").map(mdToPlain).join("\t")).join("\n") : tsv);
      }
      toast.success(`Copied ${rangeSize()} cell${rangeSize() === 1 ? "" : "s"}`);
      return true;
    } catch { return false; }
  }, [rangeNorm, rows, columnDefs, context, richLinks]);

  // ---------- Paste ------------
  const pasteAtFocus = useCallback(async (rawText) => {
    if (!focus || !canEdit) return false;
    const lines = rawText.split(/\r?\n/);
    if (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();
    if (lines.length === 0) return false;
    const grid = lines.map((l) => l.split("\t"));
    const startR = focus.r;
    const startC = focus.c;
    const nR = grid.length;
    const nC = Math.max(...grid.map((r) => r.length));

    // Determine how many rows we need to create at the bottom
    let need = 0;
    for (let dr = 0; dr < nR; dr++) if (startR + dr >= rows.length) need++;

    const created = [];
    if (need > 0) {
      // Iter 42.4 (Perf Round 2 hotfix) — batch the N empty-row creates
      // into ONE POST to /tasks/bulk-empty via onCreateManyTasks. The
      // previous parallel-Promise.all approach hammered the ingress at
      // 100+ rows and triggered HTTP 429 (Too Many Requests) during
      // large keyword uploads. Fall back to the old per-row path only
      // when the caller didn't wire onCreateManyTasks.
      if (onCreateManyTasks) {
        try {
          const many = await onCreateManyTasks(need, "bottom");
          if (Array.isArray(many)) for (const t of many) if (t) created.push(t);
        } catch (err) {
          toast.error("Failed to create rows for paste");
          return false;
        }
      } else if (onCreateTask) {
        const results = await Promise.all(
          Array.from({ length: need }, () => onCreateTask({ position: "bottom" }).catch(() => null))
        );
        for (const t of results) if (t) created.push(t);
      } else {
        toast.error("Cannot create new rows here");
        return false;
      }
    }
    const effRows = [...rows, ...created];

    // Build per-task merged patches
    const perTask = {}; // taskId → { task, patch, inverse }
    for (let dr = 0; dr < nR; dr++) {
      const cells = grid[dr];
      const task = effRows[startR + dr];
      if (!task) continue;
      for (let dc = 0; dc < cells.length; dc++) {
        const col = columnDefs[startC + dc];
        if (!col || !col.editable) continue;
        const parsed = col.parseText ? col.parseText(cells[dc], context) : cells[dc];
        if (parsed === undefined) continue;
        const oldVal = col.getVal(task);
        if (oldVal === parsed) continue;
        const cur = perTask[task.id] || { task, patch: {}, inverse: {} };
        const forwardPatch = col.setValInto(parsed, task);
        const backPatch = col.setValInto(oldVal, task);
        cur.patch = mergePatch(cur.patch, forwardPatch);
        cur.inverse = mergePatch(cur.inverse, backPatch);
        perTask[task.id] = cur;
      }
    }
    // Apply — batch via onBulkPatch if available (single HTTP round trip)
    const patchList = Object.values(perTask).map((p) => ({ task_id: p.task.id, patch: p.patch }));
    try {
      if (onBulkPatch && patchList.length > 0) await onBulkPatch(patchList);
      else for (const { task, patch } of Object.values(perTask)) { try { await onPatchTask(task, patch); } catch {} }
    } catch (e) { /* continue */ }
    const totalCells = Object.values(perTask).reduce((n, p) => n + Object.keys(p.patch).length, 0);
    if (created.length > 0 || totalCells > 0) {
      toast.success(`Pasted ${totalCells} cell${totalCells === 1 ? "" : "s"}${created.length ? ` (added ${created.length} row${created.length === 1 ? "" : "s"})` : ""}`);
    }
    // Undo op — revert patches + delete created rows. Redo re-applies via bulk endpoint.
    pushUndo({
      label: "paste",
      revert: async () => {
        const invList = Object.values(perTask).map((p) => ({ task_id: p.task.id, patch: p.inverse }));
        if (onBulkPatch && invList.length > 0) { try { await onBulkPatch(invList); } catch {} }
        else for (const { task, inverse } of Object.values(perTask)) { try { await onPatchTask(task, inverse); } catch {} }
        if (created.length > 0 && onDeleteTasks) {
          try { await onDeleteTasks(created.map((t) => t.id), created); } catch {}
        }
      },
      redo: async () => {
        if (onBulkPatch && patchList.length > 0) { try { await onBulkPatch(patchList); } catch {} }
        else for (const { task, patch } of Object.values(perTask)) { try { await onPatchTask(task, patch); } catch {} }
      },
    });
    // Expand focus range to cover pasted area
    setFocus({ r: startR, c: startC });
    setRangeEnd({ r: Math.min(startR + nR - 1, effRows.length - 1), c: Math.min(startC + nC - 1, nCols - 1) });
    return true;
  }, [focus, canEdit, rows, columnDefs, context, onPatchTask, onCreateTask, onDeleteTasks, pushUndo, nCols]);

  // ---------- Autofill ------------
  const beginAutofill = useCallback(() => {
    if (!focus) return;
    setAutofill({ fromR: rangeNorm?.r1 ?? focus.r, fromC: focus.c, toR: rangeNorm?.r2 ?? focus.r });
  }, [focus, rangeNorm]);
  const extendAutofill = useCallback((toR) => {
    setAutofill((af) => af ? { ...af, toR: Math.max(af.fromR, toR) } : af);
  }, []);
  const commitAutofill = useCallback(async () => {
    const af = autofill;
    setAutofill(null);
    if (!af || af.toR <= af.fromR || !canEdit) return;
    const col = columnDefs[af.fromC];
    const srcTask = rows[af.fromR];
    if (!col || !srcTask || !col.editable) return;
    const srcVal = col.getVal(srcTask);
    const perTask = {};
    for (let r = af.fromR + 1; r <= af.toR; r++) {
      const task = rows[r];
      if (!task) continue;
      const oldVal = col.getVal(task);
      if (oldVal === srcVal) continue;
      perTask[task.id] = {
        task,
        patch: col.setValInto(srcVal, task),
        inverse: col.setValInto(oldVal, task),
      };
    }
    if (Object.keys(perTask).length === 0) return;
    const patchList = Object.values(perTask).map((p) => ({ task_id: p.task.id, patch: p.patch }));
    const invList = Object.values(perTask).map((p) => ({ task_id: p.task.id, patch: p.inverse }));
    if (onBulkPatch) await onBulkPatch(patchList);
    else for (const { task, patch } of Object.values(perTask)) await onPatchTask(task, patch);
    pushUndo({
      label: `autofill ${Object.keys(perTask).length} cell${Object.keys(perTask).length === 1 ? "" : "s"}`,
      revert: async () => {
        if (onBulkPatch) await onBulkPatch(invList);
        else for (const { task, inverse } of Object.values(perTask)) await onPatchTask(task, inverse);
      },
      redo: async () => {
        if (onBulkPatch) await onBulkPatch(patchList);
        else for (const { task, patch } of Object.values(perTask)) await onPatchTask(task, patch);
      },
    });
    setRangeEnd({ r: af.toR, c: af.fromC });
  }, [autofill, columnDefs, rows, canEdit, onPatchTask, onBulkPatch, pushUndo]);

  // ---------- Delete tasks (with undo) ------------
  const deleteTasksWithUndo = useCallback(async (taskIds) => {
    if (!onDeleteTasks) return;
    const snapshots = rows.filter((t) => taskIds.includes(t.id));
    // Track the "currently live" ids — starts as the originals, but if undo recreates them with new ids we swap.
    let liveIds = [...taskIds];
    await onDeleteTasks(liveIds, snapshots);
    if (!onCreateTask) return; // can't undo without a way to recreate
    pushUndo({
      label: `delete ${snapshots.length} task${snapshots.length === 1 ? "" : "s"}`,
      revert: async () => {
        const newIds = [];
        for (const s of snapshots) {
          const { id, created_at, updated_at, order, ...rest } = s;
          try {
            const created = await onCreateTask({ ...rest, position: "bottom" });
            if (created && created.id) newIds.push(created.id);
          } catch {}
        }
        if (newIds.length) liveIds = newIds;
      },
      redo: async () => {
        try { await onDeleteTasks(liveIds, snapshots); } catch {}
      },
    });
  }, [rows, onDeleteTasks, onCreateTask, pushUndo]);

  // ---------- Global keyboard ------------
  useEffect(() => {
    const isFormEl = (t) => {
      const tag = (t?.tagName || "").toLowerCase();
      return tag === "input" || tag === "textarea" || t?.isContentEditable;
    };
    const onKey = async (e) => {
      const meta = e.metaKey || e.ctrlKey;
      const inForm = isFormEl(e.target);

      // Undo / Redo — global, even in inputs (allowing native undo in inputs is fine — we override only when NOT in an input)
      if (meta && !e.shiftKey && (e.key === "z" || e.key === "Z") && !inForm) {
        e.preventDefault(); await undo(); return;
      }
      if (meta && ((e.shiftKey && (e.key === "z" || e.key === "Z")) || e.key === "y" || e.key === "Y") && !inForm) {
        e.preventDefault(); await redo(); return;
      }
      // If focus in an input/textarea, don't intercept anything else
      if (inForm) return;
      if (!focus) return;

      // Copy
      if (meta && (e.key === "c" || e.key === "C")) {
        e.preventDefault(); await copySelection(); return;
      }
      // Paste — handled by paste event listener globally
      if (meta && (e.key === "v" || e.key === "V")) {
        // paste event will fire naturally; no-op here
        return;
      }
      // Navigation
      const move = (dr, dc) => { e.preventDefault(); moveFocus(dr, dc, { extend: e.shiftKey }); };
      // Cmd/Ctrl+Shift+Arrow — extend selection to the sheet edge (Google-Sheets-like)
      if (meta && e.shiftKey && (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "ArrowLeft" || e.key === "ArrowRight")) {
        e.preventDefault();
        const cur = rangeEnd || focus;
        let target;
        if (e.key === "ArrowDown")  target = { r: nRows - 1, c: cur.c };
        if (e.key === "ArrowUp")    target = { r: 0, c: cur.c };
        if (e.key === "ArrowRight") target = { r: cur.r, c: nCols - 1 };
        if (e.key === "ArrowLeft")  target = { r: cur.r, c: 0 };
        if (target) setRangeEnd(target);
        setEditing(null); setAutofill(null);
        return;
      }
      if (e.key === "ArrowUp")    return move(-1, 0);
      if (e.key === "ArrowDown")  return move(1, 0);
      if (e.key === "ArrowLeft")  return move(0, -1);
      if (e.key === "ArrowRight") return move(0, 1);
      if (e.key === "Tab")        return move(0, e.shiftKey ? -1 : 1);
      if (e.key === "Enter") {
        // If editing, blur will handle; else enter edit mode
        e.preventDefault();
        if (editing) { setEditing(null); moveFocus(1, 0); }
        else setEditing(focus);
        return;
      }
      if (e.key === "F2") { e.preventDefault(); setEditing(focus); return; }
      if (e.key === "Escape") { e.preventDefault(); setEditing(null); setAutofill(null); return; }
      if (e.key === "Delete" || e.key === "Backspace") {
        // Row-checkbox handling remains in the parent — hook only clears cells
        e.preventDefault(); await clearRange(); return;
      }
      // Any printable char with no meta → open editor
      if (!meta && e.key.length === 1 && !editing) {
        setEditing(focus);
        // The editor will get the char via its own key-down after mount; we don't preventDefault
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focus, editing, moveFocus, undo, redo, copySelection, clearRange, rangeEnd, nRows, nCols]);

  // ---------- Global paste ------------
  useEffect(() => {
    const isFormEl = (t) => {
      const tag = (t?.tagName || "").toLowerCase();
      return tag === "input" || tag === "textarea" || t?.isContentEditable;
    };
    const onPaste = async (e) => {
      if (!focus || isFormEl(e.target)) return false;
      let text = e.clipboardData?.getData("text/plain");
      // Opt-in rich paste: when the clipboard carries HTML with hyperlinks,
      // convert to TSV with markdown [label](url) so links survive the paste.
      if (richLinks) {
        const html = e.clipboardData?.getData("text/html");
        if (html) { const tsv = htmlToLinkTsv(html); if (tsv) text = tsv; }
      }
      if (!text) return false;
      e.preventDefault();
      await pasteAtFocus(text);
      return true;
    };
    window.addEventListener("paste", onPaste, true);
    return () => window.removeEventListener("paste", onPaste, true);
  }, [focus, pasteAtFocus, richLinks]);

  // ---------- cellProps helper ------------
  const cellProps = useCallback((r, c) => ({
    "data-cell": `${r}-${c}`,
    onMouseDown: (e) => onCellMouseDown(r, c, e),
    onMouseEnter: () => onCellMouseEnter(r, c),
    onClick: () => { if (!isFocused(r, c)) { setFocus({ r, c }); setRangeEnd(null); } },
    onDoubleClick: () => setEditing({ r, c }),
    className: `${isFocused(r, c) ? "cell-focused" : ""} ${isInRange(r, c) ? "cell-in-range" : ""} ${isAutofillPreview(r, c) ? "autofill-preview" : ""}`.trim(),
  }), [onCellMouseDown, onCellMouseEnter, isFocused, isInRange, isAutofillPreview]);

  const setFocusFromRow = useCallback((r, c = 0) => {
    setFocus({ r, c });
    setRangeEnd(null);
  }, []);

  // Returns task IDs in the current range (or [] if no range)
  const rangeTaskIds = useCallback(() => {
    if (!rangeNorm) return [];
    const ids = [];
    for (let r = rangeNorm.r1; r <= rangeNorm.r2; r++) {
      if (rows[r]) ids.push(rows[r].id);
    }
    return ids;
  }, [rangeNorm, rows]);

  return {
    focus, rangeEnd, editing, setEditing, rangeNorm,
    isFocused, isInRange, isAutofillPreview, isBottomRightOfRange,
    cellProps, setFocusFromRow, rangeTaskIds,
    // Actions
    undo, redo, copySelection, pasteAtFocus, clearRange, moveFocus,
    patchOneCell, deleteTasksWithUndo,
    // Autofill drag
    autofill, beginAutofill, extendAutofill, commitAutofill,
    // Utilities exposed to the parent
    pushUndo,
  };
}

// ------------ helpers ------------
function safeText(v) {
  if (v === null || v === undefined) return "";
  return String(v);
}
function mergePatch(a, b) {
  // For 'custom' key, deep-merge; otherwise shallow overwrite
  const out = { ...a };
  for (const [k, v] of Object.entries(b)) {
    if (k === "custom" && a.custom && typeof v === "object" && v) out.custom = { ...a.custom, ...v };
    else out[k] = v;
  }
  return out;
}
function emptyValueForCol(col) {
  switch (col.type) {
    case "bool": return false;
    case "number": return "";
    case "user":
    case "member": return null;
    case "select": return "";
    default: return "";
  }
}

// Copy-side helpers: stored `[label](url)` markdown → clean plain text
// (label only) or real HTML anchors.
function mdToPlain(text) {
  return String(text ?? "").replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, "$1");
}
function mdToHtml(text) {
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return String(text ?? "").replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, label, url) =>
    `<a href="${esc(url)}">${esc(label)}</a>`)
    .split(/(<a [^>]*>.*?<\/a>)/g)
    .map((part) => (part.startsWith("<a ") ? part : esc(part)))
    .join("");
}

// Convert pasted HTML (Google Sheets, web pages, docs) into TSV, preserving
// hyperlinks as markdown `[label](url)` while KEEPING surrounding plain text.
// Returns null when the HTML carries no links, so the caller falls back to
// plain text.
function nodeToMd(node) {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent || "";
  if (node.nodeType !== Node.ELEMENT_NODE) return "";
  if (node.tagName === "A" && node.getAttribute("href")) {
    const label = (node.textContent || "").trim() || node.getAttribute("href");
    return `[${label}](${node.getAttribute("href")})`;
  }
  if (node.tagName === "BR") return " ";
  return Array.from(node.childNodes).map(nodeToMd).join("");
}

export function htmlToMarkdown(html) {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    if (!doc.querySelector("a[href]")) return null;
    const md = Array.from(doc.body.childNodes).map(nodeToMd).join("").replace(/\s+/g, " ").trim();
    return md || null;
  } catch { return null; }
}

function htmlToLinkTsv(html) {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const cellText = (el) =>
      Array.from(el.childNodes).map(nodeToMd).join("").replace(/\s+/g, " ").trim();
    const table = doc.querySelector("table");
    if (table) {
      const rows = Array.from(table.querySelectorAll("tr")).map((tr) =>
        Array.from(tr.querySelectorAll("td,th")).map(cellText).join("\t")
      );
      if (rows.length) return rows.join("\n");
    }
    return htmlToMarkdown(html);
  } catch { return null; }
}
